// The pure half of companion/src/serve.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250): the
// findingId scheme the writing surface applies by (withFindingIds, serve.mjs:96-105) and the two
// mechanical fixes, R2 (snap an off-ladder price to the nearest rung) and R7 (remove a resolved
// marker line, or write the ending out when that line was where the scene ended), serve.mjs:197-268.
// The HTTP layer around them (readBody, the routes, the static files) is I/O and stays JavaScript;
// what is here reads nothing and writes nothing. Every note is the frozen text, byte for byte.
import type {
  Analysis,
  IdentifiedAnalysis,
  IdentifiedFinding,
  MechanicalRule,
  Suggestion,
} from '../../dorian-shared/types/episodeScript';
import { PRICE_LADDER } from './rules';
import { parse } from './script/parse';

const MECHANICAL: readonly MechanicalRule[] = ['R2', 'R7'];

// serve.mjs:96-105 — `findingId` is `${rule}:${line ?? 0}:${index}`; only R2 and R7 have a fix.
export function withFindingIds(result: Analysis): IdentifiedAnalysis {
  return {
    ...result,
    findings: result.findings.map((item, index): IdentifiedFinding => ({
      ...item,
      findingId: `${item.rule}:${item.line ?? 0}:${index}`,
      mechanical: (MECHANICAL as readonly string[]).includes(item.rule),
    })),
  };
}

const LADDER: readonly number[] = PRICE_LADDER;

// serve.mjs:197-200.
function nearestPrice(price: number): number {
  return LADDER.reduce((best, candidate) => (Math.abs(candidate - price) < Math.abs(best - price) ? candidate : best));
}

// A margin note that opens with the word "ending" is the adapter's spelling of
// an ending, written before "> end" existed, and the parser gives the scene its
// ending from it. Deleting that line deletes the ending: the scene falls through
// to whatever node comes next, which is a different story. This answers with the
// scene name to write the ending under when the note on that exact line is the
// one the scene's ending comes from, and null when the note carries no meaning.
// serve.mjs:208-216.
function endingNoteAt(script: string, lineNumber: number): string | null {
  const ast = parse(script);
  const scene = ast.scenes.find((candidate) => candidate.ending?.source === 'note' && candidate.ending.line === lineNumber);
  if (!scene) return null;
  return String(scene.ending?.name ?? scene.name ?? '').trim() || scene.name;
}

// serve.mjs:218-268. The payload is `unknown` at the boundary, as the HTTP body was: a script or a
// findingId that is not a string is a TypeError with the frozen message.
export function applySuggestion(payload: unknown): Suggestion {
  const body = (payload ?? {}) as { script?: unknown; findingId?: unknown };
  if (typeof body.script !== 'string') throw new TypeError('script must be a string');
  if (typeof body.findingId !== 'string') throw new TypeError('findingId must be a string');
  const { script } = body;
  const [rule, rawLine] = body.findingId.split(':');
  const lineNumber = Number(rawLine);
  const lines = script.replace(/\r\n?/g, '\n').split('\n');
  if (!Number.isInteger(lineNumber) || lineNumber < 1 || lineNumber > lines.length) {
    return { script, applied: false, note: "That tip doesn't match your script any more, so nothing changed." };
  }
  const index = lineNumber - 1;
  if (rule === 'R2') {
    let changed = false;
    lines[index] = lines[index].replace(/\[(-?\d+(?:\.\d+)?)\]/, (whole, value: string) => {
      const price = Number(value);
      if (!Number.isFinite(price) || price === 0 || LADDER.includes(price)) return whole;
      changed = true;
      return `[${nearestPrice(price)}]`;
    });
    return {
      script: changed ? lines.join('\n') : script,
      applied: changed,
      note: changed ? 'Basil moved that price to the nearest one the season uses.' : 'That price is already fine, so nothing changed.',
    };
  }
  if (rule === 'R7') {
    if (!/^\s*\?(?:\s|$)/.test(lines[index])) {
      return {
        script,
        applied: false,
        note: /^\s*>/.test(lines[index])
          ? 'Only you can say what that line should say, so nothing changed.'
          : "There's no question on that line any more, so nothing changed.",
      };
    }
    const ending = endingNoteAt(script, lineNumber);
    if (ending) {
      const indent = (/^\s*/.exec(lines[index]) as RegExpExecArray)[0];
      lines[index] = `${indent}> end: ${ending}`;
      return {
        script: lines.join('\n'),
        applied: true,
        note: 'That question was the end of the scene, so Basil turned it into an ending instead of deleting it.',
      };
    }
    lines.splice(index, 1);
    return { script: lines.join('\n'), applied: true, note: 'Basil took that question line out of your script.' };
  }
  if (rule === 'R8') {
    return { script, applied: false, note: 'Only you can trim this one, so nothing changed.' };
  }
  return { script, applied: false, note: "Basil can't make this change for you, so nothing changed." };
}
